import MDinput from '@/components/MDinput';
import Select from '@/components/DSE/Select';
import Switch from '@/components/DSE/Switch';
import TwoChoice from '@/components/DSE/TwoChoice';
import {
  validatorRequire,
  validatorRequireNumber,
  validatorConditionalRequired,
  validatorConditionalRequiredNumber,
  validatorEAN,
  validatorConditionalHasValue
} from '@/utils/validators';
import { ProductTypeOption, FabricStatusOption } from '@/utils/constants';
import InputNumber from '@/components/DSE/InputNumber';
import TimestampedRTE from '@/components/TimestampedRTE';
import DateTimePicker from '@/components/DSE/DateTimePicker';
import PickRec from '@/components/DSE/PickRec';
import { FinishTypeOption } from '@/utils/constants';
import ExcludedFinishesList from '../../components/ExcludedFinishesList';
import StockList from './components/StockList';
import StockCorrections from './components/StockCorrections';
import i18n from '@/i18n';
import { ConvertMillimetersToCentimeters } from '@/utils/converters';
import ConsumerPriceList from '@/pages/fabrics/fabrics/components/ConsumerPriceList';
import { DomainTypeOption } from '@/utils/constants';

export const validatorRequireDomainProductCode = (allDomains, selectedDomainID, ownProductYN) => (
  rule,
  value,
  callback
) => {
  if (
    !value &&
    !ownProductYN &&
    allDomains.find(i => i.id === selectedDomainID) &&
    !(allDomains.find(i => i.id === selectedDomainID).domainType === DomainTypeOption.InteriorDecorator)
  ) {
    callback(new Error(i18n.t('validation.required')));
  } else {
    callback();
  }
};

export const validatorExcludedWorkshops = (allWorkshops, excludedWorkshops) => (rule, value, callback) => {
  if (allWorkshops.length && excludedWorkshops.length >= allWorkshops.length) {
    callback(new Error(i18n.t('validation.notAllWorkshopsCanBeExcluded')));
  } else {
    callback();
  }
};

export const validatorPreferredWorkshop = (preferredWorkshop, excludedWorkshops) => (rule, value, callback) => {
  if (preferredWorkshop && excludedWorkshops.includes(preferredWorkshop)) {
    callback(new Error(i18n.t('validation.cannotExcludePreferredWorkshop')));
  } else {
    callback();
  }
};

function isAllowedDomainForOwnProduct(domainID, product) {
  if (product.linkedDomains.length === 0) {
    return true;
  }
  return product.linkedDomains.some(id => id === domainID);
}

function useOfProductIsAllowed(form, product) {
  if (form.ownProductYN && product.ownProductYN) {
    return true;
  }
  if (!form.ownProductYN && product.domainID === form.domainID) {
    return true;
  }
  return product.ownProductYN && isAllowedDomainForOwnProduct(form.domainID, product);
}

export function getFormDefinition(vue) {
  return {
    type: 'tab',
    saveButtons: [true, false, false, false, false, false], // Page, tab 1, tab 2, ...
    showFormLabels: [true, true, false, false, false, true], // Tab 1, tab 2, ....
    labels: [
      'fabric.tabGeneral',
      'fabric.tabDetails',
      'common.excludedFinishes',
      'fabric.stocks',
      'fabric.stockCorrections',
      'fabric.prices'
    ],
    headerTemplate: (str, form) => `
    <h3>
    ${vue.createHeaderText(form.description, form.domainProductCode) || ''}
    </h3>
    `,
    groups: [
      {
        description: {
          type: MDinput,
          defaultValue: '',
          props: {
            maxlength: 64,
            caption: 'fabric.description'
          },
          rules: [{ validator: validatorRequire }]
        },
        productTypeID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.productType',
            required: true,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['productType/isLoading'],
            options: vue.$store.getters['productType/loadedItems'].filter(
              productType => productType.productTypeOption === ProductTypeOption.Fabric
            )
          },
          rules: [{ validator: validatorRequireNumber }]
        },
        ean: {
          type: MDinput,
          defaultValue: null,
          props: {
            maxlength: 13
          },
          rules: [{ validator: validatorEAN }]
        },
        posCode: {
          type: MDinput,
          defaultValue: null,
          props: {
            maxlength: 7,
            caption: 'fabric.posCode'
          }
        },
        ownProductYN: {
          type: Switch,
          defaultValue: false,
          props: {
            caption: 'product.ownProduct',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        domainID: {
          type: Select,
          defaultValue: vue.$store.getters['domain/getDefaultSelectValueDomainID'],
          valueOnFormChange: (form, oldValue) => {
            if (form.ownProductYN) {
              return null;
            }
            return oldValue;
          },
          dynamicProps: form => ({
            visible: !form.ownProductYN
          }),
          dynamicRules: form => [{ validator: validatorConditionalRequired(!form.ownProductYN) }],
          props: {
            caption: 'common.domain',
            required: true,
            valueField: 'id',
            labelField: 'name',
            options: vue.$store.getters.domains
          }
        },
        domainProductCode: {
          type: MDinput,
          defaultValue: '',
          valueOnFormChange: (form, oldValue) => {
            if (form.ownProductYN) {
              return null;
            }
            return oldValue;
          },
          props: {
            maxlength: 32,
            caption: 'fabric.domainProductCode'
          },
          dynamicProps: form => ({
            visible: !form.ownProductYN
          }),
          dynamicRules: form => [
            {
              validator: validatorRequireDomainProductCode(
                vue.$store.getters['domain/loadedItems'],
                form.domainID,
                form.ownProductYN
              )
            }
          ]
        },
        collections: {
          type: Select,
          defaultValue: [],
          valueOnFormChange: (form, oldValue) => {
            if (form.ownProductYN) {
              return [];
            }
            return oldValue;
          },
          dynamicProps: form => ({
            visible: !form.ownProductYN,
            options: vue.$store.getters['collection/loadedItems'].filter(
              collection => collection.domainID === form.domainID
            )
          }),
          props: {
            caption: 'fabric.collections',
            valueField: 'id',
            labelField: 'description',
            multiple: true,
            loading: vue.$store.getters['collection/isLoading']
          }
        },
        printOnTicketYN: {
          type: Switch,
          defaultValue: true,
          props: {
            caption: 'product.printOnTicket',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        stockItemYN: {
          type: Switch,
          defaultValue: true,
          props: {
            caption: 'product.stockItem',
            activeText: 'common.yes',
            inactiveText: 'common.no'
          }
        },
        napOption: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.nap',
            required: true,
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('nap')
          },
          dynamicRules: form => [{ validator: validatorConditionalRequiredNumber(vue.requiredFieldsMustBeSet) }]
        },
        washingInstructionProductID: {
          type: Select,
          defaultValue: null,
          valueOnFormChange: (form, oldValue) => {
            const product = vue.$store.getters['product/getLoadedItemByID'](oldValue);
            return product && useOfProductIsAllowed(form, product) ? oldValue : null;
          },
          dynamicProps: form => ({
            options: vue.$store.getters['product/loadedItems'].filter(
              product =>
                product.productTypeOption === ProductTypeOption.WashingInstructions &&
                useOfProductIsAllowed(form, product)
            )
          }),
          props: {
            caption: 'fabric.washingInstructionProduct',
            placeholder: vue.$t('common.notApplicableAbbr'),
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['product/isLoading']
          }
        },
        yarnID: {
          type: Select,
          defaultValue: 0,
          props: {
            caption: 'fabric.yarn',
            required: true,
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['yarn/isLoading'],
            options: vue.$store.getters['yarn/loadedItems']
          },
          rules: [{ validator: validatorRequire }]
        },
        width: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            caption: 'fabric.width'
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(vue.requiredFieldsMustBeSet) }]
        },
        actualWidth: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            caption: 'fabric.actualWidth'
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(vue.requiredFieldsMustBeSet) }]
        },
        hasPatternYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.hasPattern'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        patternHeightInMm: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            caption: 'fabric.patternHeight',
            step: 1
          },
          valueOnFormChange: (form, oldValue) => {
            return form.hasPatternYN ? oldValue : 0;
          },
          dynamicProps: form => ({
            visible: !!form.hasPatternYN
          }),
          dynamicRules: form => [
            { validator: validatorConditionalRequired(form.hasPatternYN && vue.requiredFieldsMustBeSet) }
          ]
        },
        weightPerM2InG: {
          type: InputNumber,
          defaultValue: 0,
          props: {
            caption: 'fabric.weightPerM2InG'
          },
          dynamicRules: form => [{ validator: validatorConditionalRequired(vue.requiredFieldsMustBeSet) }]
        },
        lockSeamWidth: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'fabric.lockSeamWidth'
          },
          dynamicProps: form => {
            return {
              placeholder: vue.inheritedValuePlaceholder(vue.defaultDefaultLockSeamWidthInCm),
              tooltip:
                typeof form.lockSeamWidth === 'undefined' &&
                vue.inheritedValueFromSettingsToolTip('fabric.defaultLockSeamWidthInMm')
            };
          }
        },
        cuttingWastePercentage: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            min: 0,
            max: 20,
            caption: 'fabric.cuttingWastePercentage'
          },
          dynamicProps: form => {
            return {
              placeholder: vue.inheritedValuePlaceholder(vue.defaultCuttingWastePercentage),
              tooltip:
                typeof form.cuttingWastePercentage === 'undefined' &&
                vue.inheritedValueFromSettingsToolTip('fabric.cuttingWastePercentage')
            };
          }
        },
        flawInWeavingPercentage: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            min: 0,
            max: 100,
            caption: 'fabric.flawInWeavingPercentage'
          },
          dynamicProps: form => ({
            handleModelInputExtension: () => (form.leftOverPercentage = 100 - form.flawInWeavingPercentage)
          })
        },
        leftOverPercentage: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            min: 0,
            max: 100,
            caption: 'fabric.leftOverPercentage'
          },
          dynamicProps: form => ({
            handleModelInputExtension: () => (form.flawInWeavingPercentage = 100 - form.leftOverPercentage)
          })
        },
        partsPerHanger: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            caption: 'fabric.partsPerHanger'
          },
          dynamicProps: form => ({
            placeholder: vue.inheritedValuePlaceholder(vue.defaultPartsPerHanger),
            tooltip:
              typeof form.partsPerHanger === 'undefined' &&
              vue.inheritedValueFromSettingsToolTip('fabric.defaultPartsPerHanger')
          })
        },
        canBeUsedForRomanBlindsYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.canBeUsedForRomanBlinds'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        romanBlindCode: {
          type: MDinput,
          defaultValue: null,
          props: {
            maxlength: 4,
            caption: 'fabric.romanBlindCode'
          },
          valueOnFormChange: (form, oldValue) => {
            return form.canBeUsedForRomanBlindsYN ? oldValue : null;
          },
          dynamicProps: form => ({
            visible: !!form.canBeUsedForRomanBlindsYN
          })
        },
        romanBlindCanBeLinedYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.romanBlindCanBeLinedYN'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        cordProductID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.cordProduct',
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['product/isLoading']
          },
          dynamicProps: form => ({
            visible: !!form.canBeUsedForRomanBlindsYN,
            options: vue.$store.getters['product/loadedItems'].filter(
              product =>
                product.productTypeOption === ProductTypeOption.RomanBlindCord && useOfProductIsAllowed(form, product)
            ),
            placeholder: vue.$t('fabric.noCordProductID')
          })
        },
        romanBlindRibTypeOption: {
          type: Select,
          defaultValue: null,
          valueOnFormChange: (form, oldValue) => {
            return form.canBeUsedForRomanBlindsYN ? oldValue : null;
          },
          props: {
            caption: 'fabric.romanBlindRibTypeOption',
            required: true,
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('romanBlindRibType')
          },
          dynamicProps: form => ({
            visible: !!form.canBeUsedForRomanBlindsYN
          }),
          dynamicRules: form => [
            {
              validator: validatorConditionalRequiredNumber(
                vue.requiredFieldsMustBeSet && form.canBeUsedForRomanBlindsYN
              )
            }
          ]
        },
        canBeUsedAsLiningForRomanBlindsYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.canBeUsedAsLiningForRomanBlindsYN'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        liningCordProductID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.liningCordProduct',
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['product/isLoading']
          },
          dynamicProps: form => ({
            visible: !!form.canBeUsedAsLiningForRomanBlindsYN,
            options: vue.$store.getters['product/loadedItems'].filter(
              product =>
                product.productTypeOption === ProductTypeOption.RomanBlindCord && useOfProductIsAllowed(form, product)
            )
          }),
          valueOnFormChange: (form, oldValue) => {
            return form.canBeUsedAsLiningForRomanBlindsYN ? oldValue : null;
          }
        },
        canBeLinedYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.canBeLined'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        canBeUsedAsLiningYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.canBeUsedAsLiningYN'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        containsWeightCordYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.containsWeightCord'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        weightCordProductID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.weightCordProduct',
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['product/isLoading']
          },
          dynamicProps: form => ({
            visible: form.containsWeightCordYN === false,
            options: vue.$store.getters['product/loadedItems'].filter(
              product =>
                product.productTypeOption === ProductTypeOption.WeightCord && useOfProductIsAllowed(form, product)
            ),
            placeholder: vue.$t('fabric.noCordProductID')
          }),
          valueOnFormChange: (form, oldValue) => {
            return form.containsWeightCordYN ? null : oldValue;
          }
        },
        remarks: {
          type: TimestampedRTE
        },
        defaultSideFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Side
            )
          },
          dynamicProps: form => {
            return {
              placeholder:
                form.inheritedSideFinishDefault &&
                vue.inheritedValuePlaceholder(form.inheritedSideFinishDefault.finishName),
              tooltip:
                !form.defaultSideFinishID &&
                form.inheritedSideFinishDefault &&
                vue.inheritedValueFromProductGroupToolTip(
                  form.inheritedSideFinishDefault.source,
                  form.inheritedSideFinishDefault.productGroupName
                )
            };
          }
        },
        defaultSideFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            step: 0.5,
            precision: 1
          },
          valueOnFormChange: (form, oldValue) => {
            if (!form.defaultSideFinishID) {
              return undefined;
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              placeholder:
                form.inheritedSideFinishDefault &&
                vue.inheritedValuePlaceholder(
                  ConvertMillimetersToCentimeters(form.inheritedSideFinishDefault.measurementInMm)
                ),
              tooltip:
                typeof form.defaultSideFinishMeasurementInCm === 'undefined' &&
                form.inheritedSideFinishDefault &&
                vue.inheritedValueFromProductGroupToolTip(
                  form.inheritedSideFinishDefault.source,
                  form.inheritedSideFinishDefault.productGroupName
                )
            };
          }
        },
        defaultBottomFinishID: {
          type: Select,
          defaultValue: null,
          props: {
            valueField: 'id',
            labelField: 'description',
            loading: vue.$store.getters['finish/isLoading'],
            options: vue.$store.getters['finish/loadedItems'].filter(
              finish => finish.finishTypeOption === FinishTypeOption.Bottom
            )
          },
          dynamicProps: form => {
            return {
              placeholder:
                form.inheritedBottomFinishDefault &&
                vue.inheritedValuePlaceholder(form.inheritedBottomFinishDefault.finishName),
              tooltip:
                !form.defaultBottomFinishID &&
                form.inheritedBottomFinishDefault &&
                vue.inheritedValueFromProductGroupToolTip(
                  form.inheritedBottomFinishDefault.source,
                  form.inheritedBottomFinishDefault.productGroupName
                )
            };
          }
        },
        defaultBottomFinishMeasurementInCm: {
          type: InputNumber,
          defaultValue: undefined,
          props: {
            step: 0.5,
            precision: 1
          },
          valueOnFormChange: (form, oldValue) => {
            if (!form.defaultBottomFinishID) {
              return undefined;
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              placeholder:
                form.inheritedBottomFinishDefault &&
                vue.inheritedValuePlaceholder(
                  ConvertMillimetersToCentimeters(form.inheritedBottomFinishDefault.measurementInMm)
                ),
              tooltip:
                typeof form.defaultBottomFinishMeasurementInCm === 'undefined' &&
                form.inheritedBottomFinishDefault &&
                vue.inheritedValueFromProductGroupToolTip(
                  form.inheritedBottomFinishDefault.source,
                  form.inheritedBottomFinishDefault.productGroupName
                )
            };
          }
        },
        discontinuationDate: {
          type: DateTimePicker,
          props: {
            caption: 'fabric.discontinuationDate',
            type: 'date',
            format: vue.$store.getters.dateFormat,
            disabled: true
          },
          valueOnFormChange: (form, oldValue) => {
            if (!oldValue && form.fabricStatusOption === FabricStatusOption.Discontinued) {
              return new Date();
            }
            return oldValue;
          },
          dynamicProps: form => {
            return {
              visible: form.fabricStatusOption === FabricStatusOption.Discontinued
            };
          }
        },
        handlockYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.handlock'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        blackoutYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.blackout'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        excludedFinishes: {
          type: ExcludedFinishesList,
          props: {
            caption: 'common.emptyString'
          }
        },
        stocks: {
          type: StockList,
          props: {
            caption: 'common.emptyString'
          }
        },
        stockCorrections: {
          type: StockCorrections,
          props: {
            caption: 'common.emptyString'
          }
        },
        fabricStatusOption: {
          type: Select,
          defaultValue: FabricStatusOption.New,
          hideOnCreate: true,
          props: {
            caption: 'fabric.fabricStatusOption',
            valueField: 'code',
            labelField: 'description',
            loading: vue.$store.getters['option/isLoading'],
            options: vue.$store.getters['option/loadedOptions']('fabricStatus')
          },
          dynamicRules: form => [{ validator: validatorConditionalRequiredNumber(vue.requiredFieldsMustBeSet) }]
        },
        supplierID: {
          type: PickRec,
          defaultValue: null,
          props: {
            caption: 'common.supplier',
            valueField: 'id',
            labelField: 'agentName',
            inputField: 'name',
            maxlength: 64,
            columns: [
              { field: 'name', label: 'common.name' },
              { field: 'agentName', label: 'supplier.agentName' }
            ],
            entity: 'Supplier',
            required: true,
            usePagination: true,
            hideInput: false,
            suggest: true
          }
        },
        preferredWorkshopID: {
          type: Select,
          defaultValue: null,
          props: {
            caption: 'fabric.preferredWorkshop',
            required: false,
            valueField: 'id',
            labelField: 'name',
            loading: vue.$store.getters['workshop/isLoading'],
            options: vue.$store.getters['workshop/loadedItems']
          },
          dynamicRules: form => [
            {
              validator: validatorPreferredWorkshop(form.preferredWorkshopID, form.excludedWorkshops)
            }
          ]
        },
        excludedWorkshops: {
          type: Select,
          defaultValue: [],
          valueOnFormChange: (form, oldValue) => {
            return oldValue.filter(workshopID => workshopID !== form.preferredWorkshopID);
          },
          props: {
            caption: 'fabric.excludedWorkshops',
            valueField: 'id',
            labelField: 'name',
            multiple: true,
            options: vue.$store.getters['workshop/loadedItems'],
            loading: vue.$store.getters['workshop/isLoading']
          },
          dynamicRules: form => [
            {
              validator: validatorExcludedWorkshops(vue.$store.getters['workshop/loadedItems'], form.excludedWorkshops)
            }
          ]
        },
        ironYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.ironYN'
          },
          dynamicRules: form => [{ validator: validatorConditionalHasValue(vue.requiredFieldsMustBeSet) }]
        },
        staggeredPatternYN: {
          type: TwoChoice,
          props: {
            caption: 'fabric.staggeredPatternYN'
          },
          valueOnFormChange: (form, oldValue) => {
            return form.hasPatternYN ? oldValue : null;
          },
          dynamicProps: form => ({
            visible: !!form.hasPatternYN
          }),
          dynamicRules: form => [
            { validator: validatorConditionalHasValue(!!form.hasPatternYN && vue.requiredFieldsMustBeSet) }
          ]
        },
        purchasePrice: {
          type: MDinput,
          props: {
            caption: 'fabric.purchasePrice',
            type: 'currency',
            precision: 2
          },
          dynamicProps: form => ({
            currencyPrefix: form.currencySymbol
          })
        },
        sellingPrice: {
          type: MDinput,
          props: {
            caption: 'fabric.sellingPrice',
            type: 'currency',
            precision: 2
          },
          dynamicProps: form => ({
            currencyPrefix: form.currencySymbol
          })
        },
        consumerPrices: {
          type: ConsumerPriceList,
          props: {
            caption: 'fabric.consumerPrices',
            min: 0
          },
          dynamicProps: form => ({
            domainID: form.domainID
          })
        }
      }
    ]
  };
}

const layoutTabGeneral = {
  named: true,
  sections: [
    {
      width: 50,
      fields: [
        'description',
        'productTypeID',
        'ownProductYN',
        'domainID',
        'domainProductCode',
        'supplierID',
        'collections',
        'ean',
        'posCode'
      ]
    },
    {
      width: 50,
      fields: [
        'fabricStatusOption',
        'discontinuationDate',
        'preferredWorkshopID',
        'excludedWorkshops',
        'printOnTicketYN',
        'stockItemYN'
      ]
    },
    {
      width: 100,
      fields: ['remarks']
    }
  ]
};

const layoutTabDetails = {
  named: true,
  sections: [
    {
      width: 33,
      fields: [
        'napOption',
        'washingInstructionProductID',
        'yarnID',
        'partsPerHanger',
        'width',
        'actualWidth',
        'hasPatternYN',
        'patternHeightInMm'
      ]
    },
    {
      width: 33,
      fields: [
        'weightPerM2InG',
        'lockSeamWidth',
        'cuttingWastePercentage',
        'leftOverPercentage',
        'flawInWeavingPercentage',
        'defaultSideFinishID',
        'defaultSideFinishMeasurementInCm',
        'defaultBottomFinishID',
        'defaultBottomFinishMeasurementInCm'
      ]
    },
    {
      width: 33,
      fields: [
        'canBeLinedYN',
        'canBeUsedAsLiningYN',
        'canBeUsedForRomanBlindsYN',
        'romanBlindCode',
        'romanBlindCanBeLinedYN',
        'cordProductID',
        'romanBlindRibTypeOption',
        'canBeUsedAsLiningForRomanBlindsYN',
        'liningCordProductID',
        'containsWeightCordYN',
        'weightCordProductID',
        'handlockYN',
        'blackoutYN',
        'ironYN',
        'staggeredPatternYN'
      ]
    },
    {
      width: 100,
      fields: ['remarks']
    }
  ]
};

const layoutTabExcludedFinishes = {
  named: true,
  sections: [
    {
      fields: ['excludedFinishes']
    }
  ]
};

const layoutTabStocks = {
  named: true,
  sections: [
    {
      fields: ['stocks']
    }
  ]
};

const layoutTabStockCorrections = {
  named: true,
  sections: [
    {
      fields: ['stockCorrections']
    }
  ]
};

const layoutTabPrices = {
  named: true,
  sections: [
    {
      fields: ['sellingPrice', 'purchasePrice', 'consumerPrices']
    }
  ]
};

export function getCreateLayoutDefinition(vue) {
  return {
    groups: [layoutTabGeneral]
  };
}

export function getEditLayoutDefinition(vue) {
  return {
    groups: [
      layoutTabGeneral,
      layoutTabDetails,
      layoutTabExcludedFinishes,
      layoutTabStocks,
      layoutTabStockCorrections,
      layoutTabPrices
    ]
  };
}

export function createItemFromFormData(vue, form) {
  const item = { ...form };
  if (typeof item.defaultBottomFinishMeasurementInCm === 'undefined') {
    item.defaultBottomFinishMeasurementInCm = null;
  }

  if (typeof item.defaultSideFinishMeasurementInCm === 'undefined') {
    item.defaultSideFinishMeasurementInCm = null;
  }
  item.doNotIronYN = !form.ironYN;
  delete form.ironYN;
  return item;
}

export function createFormDataFromItem(vue, item) {
  const form = { ...item };
  const numericFieldsWithPlaceHolders = [
    'cuttingWastePercentage',
    'defaultBottomFinishMeasurementInCm',
    'defaultSideFinishMeasurementInCm',
    'lockSeamWidth',
    'partsPerHanger'
  ];
  numericFieldsWithPlaceHolders.forEach(field => {
    if (form[field] === null) {
      form[field] = undefined;
    }
  });
  form.ironYN = !item.doNotIronYN;
  return form;
}
